import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, X } from "lucide-react";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1579468118288-682e600b8565?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxzaWx2ZXIlMjBhbnRpcXVlJTIwY29pbiUyMG1hY3JvfGVufDB8fHx8MTc4MTczNzYwMXww&ixlib=rb-4.1.0&q=85";

const CoinDetail = () => {
  const { id } = useParams();
  const [coin, setCoin] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/coins/${id}`)
      .then(({ data }) => { setCoin(data); setActive(0); })
      .catch(() => setNotFound(true));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", { coin_id: id, ...form });
      setDone(true);
      toast.success("Inquiry sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send inquiry");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="px-6 md:px-12 lg:px-24 py-32 text-center">
        <p className="font-display text-4xl text-[#A19D94] mb-6">This specimen has wandered off.</p>
        <Link to="/catalog" className="coin-tag text-[#D4AF37]">← Return to catalog</Link>
      </div>
    );
  }

  if (!coin) {
    return <div className="px-6 py-32 text-center font-display text-2xl text-[#A19D94]">Loading…</div>;
  }

  const gallery = (coin.images && coin.images.length)
    ? coin.images.map(fileUrl)
    : (coin.image_url ? [fileUrl(coin.image_url)] : [FALLBACK_IMG]);
  const img = gallery[active] || gallery[0];

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16">
      <Link to="/catalog" className="coin-tag text-[#A19D94] hover:text-[#D4AF37] inline-flex items-center gap-2 mb-12" data-testid="back-to-catalog">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to Catalog
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div className="lg:sticky lg:top-28 self-start">
          <button
            type="button"
            onClick={() => setLightbox(true)}
            className="aspect-square w-full bg-[#0A0A0A] border border-[#F3EFE0]/10 p-8 flex items-center justify-center overflow-hidden cursor-zoom-in group"
            data-testid="coin-detail-image-btn"
          >
            <img src={img} alt={coin.name} className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500" data-testid="coin-detail-image" />
          </button>
          {gallery.length > 1 && (
            <div className="flex flex-wrap gap-3 mt-4" data-testid="coin-thumbnails">
              {gallery.map((g, i) => (
                <button
                  key={g + i}
                  type="button"
                  onClick={() => setActive(i)}
                  className={`w-16 h-16 bg-[#0A0A0A] border p-1 transition-colors ${i === active ? "border-[#D4AF37]" : "border-[#F3EFE0]/10 hover:border-[#F3EFE0]/30"}`}
                  data-testid={`coin-thumbnail-${i}`}
                >
                  <img src={g} alt={`${coin.name} view ${i + 1}`} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
            {[
              ["Era", coin.era],
              ["Metal", coin.metal],
              ["Year", coin.year],
              ["Origin", coin.origin],
              ["Condition", coin.condition],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="border-t border-[#F3EFE0]/10 pt-3">
                <div className="coin-tag mb-1">{k}</div>
                <div className="text-[#F3EFE0]">{v}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          {coin.sold && <div className="coin-tag text-[#5C2B29] bg-[#5C2B29]/10 inline-block px-3 py-1 mb-4 border border-[#5C2B29]/30">Sold</div>}
          {coin.featured && !coin.sold && <div className="coin-tag text-[#D4AF37] bg-[#D4AF37]/10 inline-block px-3 py-1 mb-4 border border-[#D4AF37]/30">Curator's Pick</div>}
          <div className="coin-tag mb-3 text-[#6B6A65]">{coin.metal || "Numismatic"} · {coin.era || "Historical"}</div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter mb-8 leading-none" data-testid="coin-detail-name">{coin.name}</h1>
          <div className="border-t border-b border-[#F3EFE0]/10 py-6 mb-10">
            <div className="coin-tag mb-2">Price</div>
            <div className="font-mono text-4xl text-[#D4AF37]" data-testid="coin-detail-price">${Number(coin.price).toLocaleString()}</div>
          </div>
          <div className="prose prose-invert max-w-none mb-12">
            <p className="text-[#A19D94] text-lg leading-relaxed whitespace-pre-line">{coin.description}</p>
          </div>

          {!coin.sold && (
            <div className="border border-[#F3EFE0]/10 p-8 bg-[#0A0A0A]">
              <div className="coin-tag mb-2">Inquire</div>
              <h3 className="font-display text-3xl mb-6">Contact to Acquire</h3>
              {done ? (
                <div className="text-[#D4AF37] py-6" data-testid="inquiry-success">
                  Thank you. Your inquiry has been received  expect a reply within one business day.
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="inquiry-form">
                  <div>
                    <label className="coin-tag block mb-2">Your Name</label>
                    <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full bg-transparent border-b border-[#F3EFE0]/20 focus:border-[#D4AF37] outline-none py-2 text-[#F3EFE0]" data-testid="inquiry-name" />
                  </div>
                  <div>
                    <label className="coin-tag block mb-2">Email</label>
                    <input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full bg-transparent border-b border-[#F3EFE0]/20 focus:border-[#D4AF37] outline-none py-2 text-[#F3EFE0]" data-testid="inquiry-email" />
                  </div>
                  <div>
                    <label className="coin-tag block mb-2">Phone (optional)</label>
                    <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full bg-transparent border-b border-[#F3EFE0]/20 focus:border-[#D4AF37] outline-none py-2 text-[#F3EFE0]" data-testid="inquiry-phone" />
                  </div>
                  <div>
                    <label className="coin-tag block mb-2">Message</label>
                    <textarea required rows={4} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} className="w-full bg-transparent border border-[#F3EFE0]/15 focus:border-[#D4AF37] outline-none py-3 px-3 text-[#F3EFE0] resize-none" data-testid="inquiry-message" />
                  </div>
                  <button disabled={submitting} type="submit" className="bg-[#D4AF37] text-black font-medium px-8 py-4 hover:bg-[#F1C40F] transition-colors disabled:opacity-50" data-testid="inquiry-submit">
                    {submitting ? "Sending…" : "Submit Inquiry"}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-6"
          onClick={() => setLightbox(false)}
          data-testid="coin-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(false)}
            className="absolute top-6 right-6 text-[#A19D94] hover:text-[#D4AF37] transition-colors"
            data-testid="lightbox-close"
          >
            <X className="w-8 h-8" strokeWidth={1.5} />
          </button>
          <img src={img} alt={coin.name} className="max-w-[90vw] max-h-[85vh] object-contain" onClick={(e) => e.stopPropagation()} />
          {gallery.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3" onClick={(e) => e.stopPropagation()}>
              {gallery.map((g, i) => (
                <button
                  key={g + i}
                  type="button"
                  onClick={() => setActive(i)}
                  className={`w-14 h-14 bg-[#0A0A0A] border p-1 transition-colors ${i === active ? "border-[#D4AF37]" : "border-[#F3EFE0]/20 hover:border-[#F3EFE0]/40"}`}
                  data-testid={`lightbox-thumbnail-${i}`}
                >
                  <img src={g} alt={`view ${i + 1}`} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoinDetail;
